import { components } from "@/api-schema";
import { getModelGenerationDatId } from "@/common/helpers/get-model-generation-dat-id";
import { VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import { DeviceType } from "@/common/hooks/use-device";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";
import { commonColumns } from "./_constants/common-columns";

export type ModelGenerationTableType = components["schemas"]["ModelGeneration"];

type ModelGenerationBrand = ModelGenerationTableType["modelFamily"]["brand"];
type ModelFamily = ModelGenerationTableType["modelFamily"];
type ModelGenerationVehicleKind = ModelGenerationTableType["vehicleKind"];

export const modelGenerationTableDefaultColumns = {
  columnName: commonColumns.columnName<ModelGenerationTableType>(),
  columnDatId: commonColumns.columnDatId<ModelGenerationTableType>((modelGeneration) =>
    getModelGenerationDatId(modelGeneration.modelGenerationIdentifiers),
  ),

  columnVehicleKind: {
    id: "vehicleKind",
    size: 140,
    enableSorting: true,
    header: () => i18next.t("masterdata:VehicleKind"),
    accessorFn: (row: ModelGenerationTableType) => row.vehicleKind,
    cell: (cell) => {
      const vehicleKind = cell.getValue() as ModelGenerationVehicleKind;
      const language = i18next.language === "de" ? "de" : "en";

      return automaticCellRenderer(VEHICLE_KIND_LABELS[vehicleKind]?.[language] ?? vehicleKind, {
        convertString: false,
      });
    },
  } as ImplementedAccessorColumnDef<ModelGenerationTableType, ModelGenerationVehicleKind>,

  columnBrand: {
    id: "brand",
    size: 120,
    enableSorting: true,
    header: () => i18next.t("common:Brand"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: ModelGenerationTableType) => row.modelFamily?.brand,
    cell: (cell) => {
      const brand = cell.getValue() as ModelGenerationBrand | undefined;

      if (!brand?.name) {
        return <EmptyValue />;
      }

      return automaticCellRenderer(brand.name);
    },
  } as ImplementedAccessorColumnDef<ModelGenerationTableType, ModelGenerationBrand | undefined>,

  columnModelFamily: {
    id: "modelFamily",
    size: 140,
    enableSorting: true,
    header: () => i18next.t("masterdata:ModelFamily"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: ModelGenerationTableType) => row.modelFamily,
    cell: (cell) => {
      const modelFamily = cell.getValue() as ModelFamily | undefined;

      if (!modelFamily?.name) {
        return <EmptyValue />;
      }

      return automaticCellRenderer(modelFamily.name, { convertString: false });
    },
  } as ImplementedAccessorColumnDef<ModelGenerationTableType, ModelFamily | undefined>,

  columnUuid: commonColumns.columnUuid<ModelGenerationTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<ModelGenerationTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<ModelGenerationTableType>(),
};
